import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AuthService from '../../../lib/authService';
import LeftPanel from './LeftPanel';
import { pushCleverTapEvent } from '../pushCleverTapEvent';

const schema = yup.object({
  email: yup.string().email('Invalid email format').required('Email is required'),
});

const ForgotPassword = ({ onBackToLogin, onClose, removeSidebarLogin }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      const response = await AuthService.forgotPassword(data.email);
      if(response?.result === true && response?.status === 200) {
        window.gtag('event', 'send_reset_link');
         pushCleverTapEvent("send_reset_link",{email: data.email});
        setSuccess(true);
      } else {
        throw new Error(response?.error || 'Failed to send reset link.');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to send reset link. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (success && removeSidebarLogin) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success,removeSidebarLogin]);  

  // ✅ SUCCESS VIEW
  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-[20px] shadow-xl w-full max-w-3xl flex overflow-hidden">
        <LeftPanel />

        {/* Right Panel */}
        <div className="w-[55%] sm:w-full p-8 relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="max-w-md mx-auto">
              <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg mb-6">
                <div className="flex items-center flex-col">
                  <svg className="w-5 h-5 mr-2" fill="green" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Mail sent to your Email ID <b>{getValues('email')}</b> successfully
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2 sm:text-lg">Forgot your password?</h3>
              <p className="text-gray-600 mb-8">
                Please check your inbox and follow the link to reset your password.
              </p>
              {!removeSidebarLogin && 
              <div className="space-y-6">
                <button
                  onClick={onBackToLogin}
                  className="w-full bg-brand text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-brand focus:ring-offset-2 transition-all"
                >
                  Back to Login
                </button>
              </div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ DEFAULT VIEW
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-[20px] shadow-xl w-full max-w-3xl flex overflow-hidden">
        <LeftPanel />

        {/* Right Panel */}
        <div className="w-[55%] p-8 relative sm:w-full sm:p-3">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-bold text-black mb-xsp">Forgot your password?</h3>
            <p className="text-[#979797] text-xs mb-3">Enter your email so that we can send you a password reset link.</p>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='mt-3'>
                <label className="block text-sm font-medium text-gray-700 mb-1">Enter Email</label>
                <input
                  type="email"
                  {...register('email')}
                  placeholder="Enter registered Email"
                  className="w-full p-1 border border-gray-300 sm:text-sm rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand text-white py-2 sm:py-1 px-1 text-sm rounded-lg font-semibold hover:bg-blue-700 focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all mt-20 sm:mt-6"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending Reset Link...
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>
            {error && (
              <div className="text-red-700 rounded-lg my-1 text-sm">
                {error}
              </div>
            )}
            
            {!removeSidebarLogin && 
            <div className="mt-6 text-center">
              <button
                onClick={onBackToLogin}
                className="text-brand font-medium hover:underline flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to login
              </button>
            </div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
